import React, { useEffect, useState } from 'react';
import { ClaimedLoanView } from '../components/ClaimedLoanView';
import { RetentionActionModal } from '../components/RetentionActionModal';
import { UpNextStrip } from '../components/UpNextStrip';
import { ReadyToClaimPanel } from '../components/ReadyToClaimPanel';
import { Loan, RetentionOutcome } from '../types';
import { AnimatePresence, motion } from 'framer-motion';
import { getDaysInRetention } from '../utils/retention';
// MOCK DATA GENERATION
const generateMockData = () => {
  const activeLoans: Loan[] = [{
    id: '1',
    loanNumber: 'LN-2024-83921',
    borrowerName: 'Sarah Jenkins',
    borrowerPhone: '(512) 555-0147',
    loanOfficer: 'Michael Scott',
    pushedBy: 'Dwight Schrute',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
    retentionComment: 'Borrower unresponsive - 3 attempts made',
    status: 'Active',
    amount: 450000
  }, {
    id: '2',
    loanNumber: 'LN-2024-83925',
    borrowerName: 'Robert California',
    borrowerPhone: '(310) 555-0293',
    loanOfficer: 'Jim Halpert',
    pushedBy: 'Andy Bernard',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 6).toISOString(),
    retentionComment: 'Rate lock expired - borrower reconsidering',
    status: 'Active',
    amount: 620000
  }, {
    id: '3',
    loanNumber: 'LN-2024-83930',
    borrowerName: 'Stanley Hudson',
    borrowerPhone: '(215) 555-0184',
    loanOfficer: 'Phyllis Vance',
    pushedBy: 'Angela Martin',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 1).toISOString(),
    retentionComment: 'Missing income documentation for co-borrower',
    status: 'Active',
    amount: 325000
  }, {
    id: '4',
    loanNumber: 'LN-2024-83942',
    borrowerName: 'Kelly Kapoor',
    borrowerPhone: '(469) 555-0321',
    loanOfficer: 'Ryan Howard',
    pushedBy: 'Toby Flenderson',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 14).toISOString(),
    retentionComment: 'Appraisal came in low - contesting value',
    status: 'Active',
    amount: 290000
  }, {
    id: '5',
    loanNumber: 'LN-2024-83955',
    borrowerName: 'Kevin Malone',
    borrowerPhone: '(602) 555-0178',
    loanOfficer: 'Oscar Martinez',
    pushedBy: 'Angela Martin',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 4).toISOString(),
    retentionComment: 'DTI ratio exceeds guidelines after credit refresh',
    status: 'Active',
    amount: 410000
  }, {
    id: '6',
    loanNumber: 'LN-2024-83961',
    borrowerName: 'Meredith Palmer',
    borrowerPhone: '(813) 555-0256',
    loanOfficer: 'Creed Bratton',
    pushedBy: 'Pam Beesly',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 8).toISOString(),
    retentionComment: 'Title issues discovered on property history',
    status: 'Active',
    amount: 185000
  }, {
    id: '7',
    loanNumber: 'LN-2024-83977',
    borrowerName: 'Erin Hannon',
    borrowerPhone: '(720) 555-0199',
    loanOfficer: 'Andy Bernard',
    pushedBy: 'Gabe Lewis',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 0.5).toISOString(),
    retentionComment: 'Employment verification failed',
    status: 'Active',
    amount: 340000
  }, {
    id: '8',
    loanNumber: 'LN-2024-83989',
    borrowerName: 'Darryl Philbin',
    borrowerPhone: '(404) 555-0342',
    loanOfficer: 'Jim Halpert',
    pushedBy: 'Val Johnson',
    dateEntered: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
    retentionComment: 'Borrower requested hold due to family emergency',
    status: 'Active',
    amount: 515000
  }];
  return {
    activeLoans
  };
};
const CURRENT_USER = 'Jane Cooper';
export function RetentionCenter() {
  const [data, setData] = useState(generateMockData());
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lastReleasedId, setLastReleasedId] = useState<string | null>(null);
  const [revivedThisWeek, setRevivedThisWeek] = useState(11);
  // Auto-release expired claims (older than 1 hour)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const oneHour = 60 * 60 * 1000;
      setData(prev => {
        let hasChanges = false;
        const updatedLoans = prev.activeLoans.map(loan => {
          if (loan.claimedAt) {
            const claimTime = new Date(loan.claimedAt).getTime();
            if (now - claimTime >= oneHour) {
              hasChanges = true;
              return {
                ...loan,
                claimedBy: undefined,
                claimedAt: undefined
              };
            }
          }
          return loan;
        });
        return hasChanges ? {
          ...prev,
          activeLoans: updatedLoans
        } : prev;
      });
    }, 10000); // Check every 10 seconds
    return () => clearInterval(interval);
  }, []);
  const claimedLoan = data.activeLoans.find(loan => loan.claimedBy === CURRENT_USER);
  const unclaimedCount = data.activeLoans.filter(loan => !loan.claimedBy).length;
  const handleClaimNext = () => {
    const nextLoan = data.activeLoans.find(loan => !loan.claimedBy && loan.id !== lastReleasedId);
    if (nextLoan) {
      setData(prev => ({
        ...prev,
        activeLoans: prev.activeLoans.map(loan => loan.id === nextLoan.id ? {
          ...loan,
          claimedBy: CURRENT_USER,
          claimedAt: new Date().toISOString()
        } : loan)
      }));
    }
  };
  const handleRelease = (loanId: string) => {
    setLastReleasedId(loanId);
    setData(prev => ({
      ...prev,
      activeLoans: prev.activeLoans.map(loan => loan.id === loanId ? {
        ...loan,
        claimedBy: undefined,
        claimedAt: undefined
      } : loan)
    }));
  };
  const handleReleaseAndClaimNext = (loanId: string) => {
    setLastReleasedId(loanId);
    setData(prev => {
      const nextLoan = prev.activeLoans.find(loan => !loan.claimedBy && loan.id !== loanId);
      return {
        ...prev,
        activeLoans: prev.activeLoans.map(loan => {
          if (loan.id === loanId) {
            return {
              ...loan,
              claimedBy: undefined,
              claimedAt: undefined
            };
          }
          if (nextLoan && loan.id === nextLoan.id) {
            return {
              ...loan,
              claimedBy: CURRENT_USER,
              claimedAt: new Date().toISOString()
            };
          }
          return loan;
        })
      };
    });
  };
  const handleActionClick = (loan: Loan) => {
    if (loan.claimedBy === CURRENT_USER) {
      setSelectedLoan(loan);
      setIsModalOpen(true);
    }
  };
  const handleActionComplete = (loanId: string, outcome: RetentionOutcome, notes: string) => {
    setRevivedThisWeek(count => count + 1);
    setData(prev => ({
      ...prev,
      activeLoans: prev.activeLoans.filter(l => l.id !== loanId)
    }));
  };
  const upNextLoans = data.activeLoans.filter(loan => !loan.claimedBy);
  const nextInLine = upNextLoans.find(loan => loan.id !== lastReleasedId) ?? upNextLoans[0];
  const avgDays = upNextLoans.length ? upNextLoans.reduce((total, loan) => total + getDaysInRetention(loan.dateEntered), 0) / upNextLoans.length : 0;
  return <div className="min-h-screen bg-brand-50 font-sans text-brand-500" data-id="element-301" data-mp="5lx1vw1xetzw">
      <main className="mx-auto flex min-h-screen max-w-[1600px] flex-col gap-4 px-5 py-6 sm:px-8 sm:py-7" data-id="element-302" data-mp="2bcshyp5wxyll">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-303" data-mp="27d0hz0ul50ru">
          <div data-id="element-304" data-mp="2vx5bjns2nr28">
            <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.14em] text-brand-300" data-id="element-305" data-mp="2vxovfa1a8piw">
              Retention Center
            </span>
            <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-brand-500" data-id="element-306" data-mp="30tngoorbdrf3">
              Claim Next Retention Loan
            </h1>
            <p className="mt-1.5 max-w-xl text-[13px] leading-snug text-brand-400" data-id="element-307" data-mp="39n8vu97e5ap1">
              Claim one retention loan at a time, work that file, then release
              it back to the bottom of the queue.
            </p>
          </div>
          <p className="flex items-center gap-2.5 rounded-full border border-brand-200 bg-white px-4 py-2 text-[13px] text-brand-400 shadow-card" data-id="element-308" data-mp="2hz8i308x00rs">
            <span className="h-[7px] w-[7px] rounded-full bg-emerald-500" aria-hidden="true" data-id="element-309" data-mp="2qy2h0x9px38i" />
            <span className="font-semibold text-brand-500" data-id="element-310" data-mp="188pl5b896uv">
              {unclaimedCount}
            </span>
            unclaimed loans remaining
          </p>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden rounded-[28px] border border-brand-200 bg-white shadow-focus" data-id="element-311" data-mp="ga71wpljfaps">
          <AnimatePresence mode="wait" initial={false} data-id="element-312" data-mp="bd75l6ehpmbv">
            {claimedLoan ? <ClaimedLoanView key={claimedLoan.id} loan={claimedLoan} hasNextLoan={upNextLoans.length > 0} onRelease={handleRelease} onReleaseAndClaimNext={handleReleaseAndClaimNext} onOpenLoan={handleActionClick} onAction={handleActionComplete} data-id="element-313" data-mp="6k5qoyea9msa" /> : <ReadyToClaimPanel key="empty" nextLoan={nextInLine} queue={upNextLoans} onClaimNext={handleClaimNext} data-id="element-314" data-mp="3jt0fv3i8a1kh" />}
          </AnimatePresence>

          <UpNextStrip loans={upNextLoans} avgDays={avgDays} revivedThisWeek={revivedThisWeek} activeClaims={claimedLoan ? 1 : 0} data-id="element-315" data-mp="1hyj6dfyvuvx8" />
        </div>
      </main>

      <RetentionActionModal loan={selectedLoan} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onAction={handleActionComplete} data-id="element-316" data-mp="15bn6orkrq6dq" />
    </div>;
}